import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';

// Converts "HH:MM" (24h) -> { hour12, minute, period }
const parse24h = (value) => {
  if (!value) return { hour12: 9, minute: 0, period: 'AM' };
  const [hStr, mStr] = value.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return { hour12: 9, minute: 0, period: 'AM' };

  let hour12 = h % 12;
  if (hour12 === 0) hour12 = 12;
  const period = h >= 12 ? 'PM' : 'AM';
  return { hour12, minute: m, period };
};

// Converts { hour12, minute, period } -> "HH:MM" (24h)
const to24h = (hour12, minute, period) => {
  let h = hour12 % 12;
  if (period === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const clampHour = (n) => {
  if (Number.isNaN(n)) return 12;
  if (n < 1) return 1;
  if (n > 12) return 12;
  return n;
};

const clampMinute = (n) => {
  if (Number.isNaN(n)) return 0;
  if (n < 0) return 0;
  if (n > 59) return 59;
  return n;
};

export const TimePicker12h = ({ value, onChange }) => {
  const { hour12, minute, period } = parse24h(value);

  const updateHour = (raw) => {
    const num = clampHour(parseInt(raw, 10));
    onChange(to24h(num, minute, period));
  };

  const updateMinute = (raw) => {
    const num = clampMinute(parseInt(raw, 10));
    onChange(to24h(hour12, num, period));
  };

  const updatePeriod = (newPeriod) => {
    onChange(to24h(hour12, minute, newPeriod));
  };

  return (
    <div className="flex items-center gap-2" data-testid="time-picker-12h">
      <Input
        type="number"
        min={1}
        max={12}
        value={hour12}
        onChange={(e) => updateHour(e.target.value)}
        className="bg-gray-800/50 border-gray-700 text-white w-16 text-center"
        data-testid="time-picker-hour"
        aria-label="Hour"
      />
      <span className="text-white text-lg font-light">:</span>
      <Input
        type="number"
        min={0}
        max={59}
        value={String(minute).padStart(2, '0')}
        onChange={(e) => updateMinute(e.target.value)}
        className="bg-gray-800/50 border-gray-700 text-white w-16 text-center"
        data-testid="time-picker-minute"
        aria-label="Minute"
      />
      <div className="flex rounded-md overflow-hidden border border-gray-700 ml-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => updatePeriod('AM')}
          className={`rounded-none px-3 h-9 text-xs font-medium ${
            period === 'AM'
              ? 'bg-teal-500/20 text-teal-300 hover:bg-teal-500/30 hover:text-teal-200'
              : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
          data-testid="time-picker-am"
        >
          AM
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => updatePeriod('PM')}
          className={`rounded-none px-3 h-9 text-xs font-medium ${
            period === 'PM'
              ? 'bg-teal-500/20 text-teal-300 hover:bg-teal-500/30 hover:text-teal-200'
              : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
          data-testid="time-picker-pm"
        >
          PM
        </Button>
      </div>
    </div>
  );
};

export default TimePicker12h;
